import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Copy, Building2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || searchParams.get('reference') || searchParams.get('trxref');
  const gateway = searchParams.get('gateway') || 'stripe';
  const bookingId = searchParams.get('booking_id');
  
  const [status, setStatus] = useState(gateway === 'bank_transfer' ? 'bank_transfer' : 'processing');
  const [bankDetails, setBankDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (gateway === 'bank_transfer') {
      fetchBankDetails();
    } else if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, gateway]);

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/bank-transfer-details`);
      setBankDetails(response.data);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  };

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}?gateway=${gateway}`);
      
      if (response.data.payment_status === 'paid' || response.data.status === 'success') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired' || response.data.status === 'failed') {
        setStatus('failed');
        return;
      }
      
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        <div className="card-clinical text-center">
          {status === 'processing' && (
            <>
              <div className="w-20 h-20 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-sky-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Processing Payment</h1>
              <p className="text-muted-foreground mb-6">Please wait while we confirm your payment...</p>
              <div className="flex justify-center">
                <div className="flex gap-1">
                  {[...Array(3)].map((_, i) => (
                    <span 
                      key={i}
                      className="w-2 h-2 bg-sky-600 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {status === 'bank_transfer' && (
            <>
              <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Bank Transfer Details</h1>
              <p className="text-muted-foreground mb-6">
                Please transfer the exact amount to the account below and use the reference as your transfer narration.
              </p>
              
              {bankDetails && (
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-6 text-left space-y-4">
                  <div className="flex justify-between items-center border-b border-border pb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Bank Name</p>
                      <p className="font-bold text-foreground">{bankDetails.bank_name}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankDetails.bank_name, 'Bank name')}
                      data-testid="copy-bank-name"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-border pb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Number</p>
                      <p className="font-bold text-foreground text-lg">{bankDetails.account_number}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankDetails.account_number, 'Account number')}
                      data-testid="copy-account-number"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Name</p>
                      <p className="font-bold text-foreground">{bankDetails.account_name}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankDetails.account_name, 'Account name')}
                      data-testid="copy-account-name"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {sessionId && (
                <div className="bg-sky-50 dark:bg-sky-900/20 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Payment Reference</p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="font-mono font-bold text-sky-600">{sessionId}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(sessionId, 'Reference')}
                      data-testid="copy-reference"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-6">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-300 text-left">
                  Your booking will be confirmed once payment is verified by our team. This usually takes a few minutes.
                </p>
              </div>
              
              <div className="space-y-3">
                <Button 
                  className="w-full bg-sky-600 hover:bg-sky-700"
                  onClick={() => navigate('/bookings')}
                  data-testid="view-bookings-btn"
                >
                  View My Bookings
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6 animate-scale-in">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground mb-6">
                Your booking has been confirmed. You will receive a confirmation email shortly.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-sky-600 hover:bg-sky-700"
                  onClick={() => navigate('/bookings')}
                  data-testid="view-bookings-btn"
                >
                  View My Bookings
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {status === 'timeout' ? 'Status Unknown' : 'Payment Failed'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {status === 'timeout' 
                  ? 'We could not verify your payment status. Please check your email or contact support.'
                  : 'Your payment could not be processed. Please try again.'}
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-sky-600 hover:bg-sky-700"
                  onClick={() => navigate('/bookings')}
                  data-testid="retry-btn"
                >
                  View Bookings
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/tests')}
                >
                  Browse Tests
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Support Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Need help? Contact us at{' '}
            <a href="mailto:FACTUALMEDICALDIAGNOSTICCENTRE@GMAIL.COM" className="text-sky-600 hover:text-sky-700">
              FACTUALMEDICALDIAGNOSTICCENTRE@GMAIL.COM
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
